import React from 'react';
import PropTypes from 'prop-types';
import { Tile, TileContent } from '@one-thd/sui-atomic-components';
import { AssistantIcon } from '../../assets/AssistantIcon';
import { MagicMarkdown } from '../common/MagicMarkdown';
import { stripMarkdown } from '../../utils/stripMarkdown';

/**
 * The design's tile carries no outline: a white fill on the peach card, lifted by a shadow. Stencil
 * has no borderless `Tile` — `filled` is the grey #F3F3F3, not white — so `outline` stays for its
 * fill, height and radius and only the border colour is dropped. The 1px stays in place as
 * transparent, so nothing shifts by a pixel when the line goes.
 */
const TILE_STYLE = { borderColor: 'transparent' };

/**
 * An inline `borderColor` also outranks Tile's `hover:sui-border-accent`, so the hover affordance
 * moves to the shadow, on the item that wraps the tile — the same arrangement `QuestionPills` uses.
 * `rounded-lg` matches Tile's own radius, so the shadow keeps the tile's shape. In a `flex-col` list
 * with `items-start` the item is already only as wide as its tile, so the shadow tracks it.
 */
const TILE_ITEM_CLASSES = 'sui-max-w-full sui-rounded-lg sui-shadow-base hover:sui-shadow-lg';

const ASK_YOUR_OWN = 'Ask your own question';

/**
 * A question tile: the assistant icon beside the question, in a borderless white Tile.
 *
 * The label goes through `MagicMarkdown` even when it is the static `ASK_YOUR_OWN` copy, so every
 * tile in the column takes its type from one place; plain text through it is just plain text.
 */
const QuestionTile = ({ label, onClick }) => (
  <Tile variant="outline" style={TILE_STYLE} onClick={onClick}>
    <TileContent alignItems="start">
      <AssistantIcon color="brand" height={24} width={21} />
      <MagicMarkdown variant="tile">{label}</MagicMarkdown>
    </TileContent>
  </Tile>
);

QuestionTile.displayName = 'QuestionTile';

QuestionTile.propTypes = {
  label: PropTypes.string,
  onClick: PropTypes.func,
};

export const SuggestedQuestions = React.memo(({ questions = [], maxQuestions, onAskQuestion }) => {
  // The cap is applied after the text filter, so a text-less question is dropped without costing a
  // slot — later questions backfill and the column still fills to `maxQuestions`. An absent
  // `maxQuestions` renders however many were passed.
  //
  // Text-less is measured by what the markdown reduces to rather than by whether there is a string,
  // for the reason `QuestionPills` gives: a tile carrying only markup would report its click as
  // `Ask your own question`.
  const visibleQuestions = questions
    .filter((question) => stripMarkdown(question.text))
    .slice(0, maxQuestions);

  // With no questions there is no column to close, so the ask-your-own tile goes too and the summary
  // takes the full width. Variant A pairs the tile with questions; variant B's pill stands alone.
  if (visibleQuestions.length === 0) {
    return null;
  }

  const handleSelect = (question) => {
    if (onAskQuestion) {
      onAskQuestion(question.text, question.position);
    }
  };

  // A click with no question behind it opens the assistant empty, for the shopper to type their own
  // question — the same handoff `QuestionPills` makes from its ask-your-own pill. The label stays out
  // of it: Magic Apron names this click for analytics itself.
  const handleAskYourOwn = () => {
    if (onAskQuestion) {
      onAskQuestion();
    }
  };

  return (
    <ul
      aria-label="Suggested questions"
      className="sui-flex sui-flex-col sui-items-start sui-gap-3 sui-list-none sui-p-0 sui-m-0"
    >
      {visibleQuestions.map((question) => (
        <li key={question.position} className={TILE_ITEM_CLASSES}>
          <QuestionTile label={question.text} onClick={() => handleSelect(question)} />
        </li>
      ))}
      {/* The design closes the column with this tile, after the questions the layout asked for. */}
      <li className={TILE_ITEM_CLASSES}>
        <QuestionTile label={ASK_YOUR_OWN} onClick={handleAskYourOwn} />
      </li>
    </ul>
  );
});

SuggestedQuestions.displayName = 'SuggestedQuestions';

SuggestedQuestions.propTypes = {
  /** How many questions the layout shows; omitted renders every question with text. */
  maxQuestions: PropTypes.number,
  /**
   * Called when a tile is clicked, with `(question, position)`. The ask-your-own tile calls it with
   * no question at all, which opens the drawer empty instead of asking.
   */
  onAskQuestion: PropTypes.func,
  /** Questions to render. `text` is markdown; entries whose markdown reduces to no text are skipped. */
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number.isRequired,
      text: PropTypes.string
    })
  )
};
